import React, { useEffect, useLayoutEffect, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { BUSINESS } from '@/lib/businessConfig';
import { recordLang } from '@/lib/cmsLang';
import NotFoundPage from './NotFoundPage';

const BlogArticlePage = React.lazy(() => import('@/pages/BlogArticlePage'));
const LandingPageView = React.lazy(() => import('@/pages/LandingPageView'));

/**
 * Loader for a native Base44 page whose editorial content lives in the CMS.
 * The route is owned by a real page component; this only fetches the ONE
 * content record that page is bound to. Missing record -> controlled 404,
 * never generic homepage content.
 *
 * lang: 'el' (default, URL /<slug>) or 'en' (URL /en/<slug>). A record whose own
 * language differs from the route's is treated as not found, so an EN record can
 * never render at a Greek URL (and vice versa).
 */
export default function CmsPage({ slug, kind = 'landing', lang = 'el' }) {
  const [state, setState] = useState('loading'); // 'loading' | 'ready' | 'notfound'
  const [data, setData] = useState(null);
  const [alternate, setAlternate] = useState(null);

  // Neutral brand title on first paint, so no title is ever derived from the
  // component name. The view (or Base44 native meta tags) sets the real title.
  useLayoutEffect(() => {
    document.title = BUSINESS.brandName;
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    const fn = kind === 'article' ? 'getPublicBlogArticle' : 'getPublicLandingPage';
    base44.functions.invoke(fn, { slug, lang })
      .then((r) => {
        const record = kind === 'article'
          ? (r.data?.article?.published ? r.data.article : null)
          : (r.data?.page || null);
        return { record: record && recordLang(record) === lang ? record : null, alternate: r.data?.alternate || null };
      })
      .catch(() => ({ record: null, alternate: null }))
      .then(({ record, alternate: alt }) => {
        if (cancelled) return;
        if (record) { setData(record); setAlternate(alt); setState('ready'); }
        else setState('notfound');
      });
    return () => { cancelled = true; };
  }, [slug, kind, lang]);

  if (state === 'loading') return <div className="bg-cream-bg min-h-screen" />;
  if (state === 'notfound') return <NotFoundPage />;
  return kind === 'article'
    ? <BlogArticlePage initialArticle={data} lang={lang} alternate={alternate} />
    : <LandingPageView initialPage={data} lang={lang} alternate={alternate} />;
}
